import { supabaseGestionPension as supabase } from '../../../../config/settings/supabase-client'
import { REACT_APP_BASE_URL } from '../../../../config/settings/environment'
import routes, { normalizeAppPath } from '../../../../config/settings/routes'
import { resolveAuthAvatarUrl } from '../../../../config/settings/avatar'
import { onNewUserSignup } from '../../../../services/signupLifecycle'

export const ACCOUNT_INACTIVE_REASON = 'account_inactive'

export const ACCOUNT_INACTIVE_MESSAGE =
  'Tu cuenta está desactivada. Escribe a soporte.'

export const buildAccountInactiveLoginPath = () =>
  `${routes.login}?reason=${ACCOUNT_INACTIVE_REASON}`

export const isAccountActive = (status) => status === 'active'

export const isStaleAuthUserError = (error) => {
  if (!error) return false
  const code = error.code || error.name
  const message = String(error.message || '').toLowerCase()
  return (
    code === 'user_not_found' ||
    code === 'invalid_session' ||
    message.includes('sub claim') ||
    message.includes('user from sub') ||
    message === 'invalid_session'
  )
}

/**
 * If the profile is inactive, sign out locally.
 * @returns {Promise<boolean>} true when the session was cleared
 */
export const signOutIfInactive = async (status) => {
  if (!status || isAccountActive(status)) return false
  await supabase.auth.signOut({ scope: 'local' })
  return true
}

const throwInvalidSession = () => {
  const stale = new Error('INVALID_SESSION')
  stale.code = 'invalid_session'
  throw stale
}

/**
 * Sign in with Google
 */
export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${REACT_APP_BASE_URL}${routes.authCallback}`,
      queryParams: {
        prompt: 'select_account',
      },
    }
  })

  if (error) throw error
  return data
}

/**
 * Get the current session
 */
export const getSession = async () => {
  const { data, error } = await supabase.auth.getSession()
  return { session: data.session, error }
}

/**
 * Sign out the current session
 */
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Get a user's profile.
 */
export const getProfile = async () => {
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    return null
  }

  const { data: profile, error } = await supabase
    .from('profiles')
    .select('id, avatar_url, full_name, onboarding_status, status')
    .eq('id', user.id)
    .single()

  const profileMapped = {
    avatarUrl: profile.avatar_url,
    email: profile.email,
    fullName: profile.full_name,
    id: profile.id,
    status: profile.status,
    onboardingStatus: profile.onboarding_status
  }

  if (error) {
    throw error
  }

  return {
    ...user,
    profileMapped
  }
}

const mapSyncedProfile = (row) => ({
  avatarUrl: row.avatar_url,
  id: row.id,
  fullName: row.full_name,
  email: row.email,
  status: row.status,
  onboardingStatus: row.onboarding_status,
})

/**
 * Sync the user's profile in the table `profiles`.
 * No longer inserts roles into `acl_user_roles` from frontend.
 */
export const syncUserProfile = async (authUser) => {
  if (!authUser) return null

  // Prefer server-validated user over getSession() cache (stale/deleted users).
  const { data: authProbe, error: authProbeError } = await supabase.auth.getUser()

  if (authProbeError || !authProbe?.user || authProbe.user.id !== authUser.id) {
    await supabase.auth.signOut({ scope: 'local' })
    throwInvalidSession()
  }

  const verifiedUser = authProbe.user

  // Check if user exists
  const { data: existingUserProfile, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', verifiedUser.id)
    .maybeSingle()

  if (error) throw error

  const applyProfileUpdates = async (currentProfile) => {
    const updates = {
      avatar_url:
        resolveAuthAvatarUrl(verifiedUser.user_metadata) ||
        currentProfile.avatar_url,
      full_name: verifiedUser.user_metadata?.full_name || currentProfile.full_name,
      email: verifiedUser.email || currentProfile.email,
      onboarding_status: currentProfile.onboarding_status || null
    }

    const { data: updatedProfile, error: updateError } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', verifiedUser.id)
      .select()
      .maybeSingle()

    if (updateError) {
      console.error('Error updating profile:', updateError)
      throw updateError
    }

    return mapSyncedProfile(updatedProfile)
  }

  // If exists, update avatar_url / full_name / email if changed
  if (existingUserProfile) {
    return applyProfileUpdates(existingUserProfile)
  }

  // If not exists, insert new
  const { data: insertedProfile, error: insertError } = await supabase
    .from('profiles')
    .insert({
      avatar_url: resolveAuthAvatarUrl(verifiedUser.user_metadata),
      email: verifiedUser.email,
      full_name: verifiedUser.user_metadata?.full_name || null,
      id: verifiedUser.id,
      status: 'active'
    })
    .select()
    .single()

  if (insertError) {
    // Parallel SIGNED_IN + INITIAL_SESSION: the other request already inserted.
    if (insertError.code === '23505') {
      const { data: racedProfile, error: racedError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', verifiedUser.id)
        .maybeSingle()

      if (racedError) throw racedError
      if (racedProfile) return applyProfileUpdates(racedProfile)
    }

    // profiles_id_fkey: session points to a user missing from auth.users
    if (insertError.code === '23503') {
      await supabase.auth.signOut({ scope: 'local' })
      throwInvalidSession()
    }

    console.error('Error inserting profile:', insertError)
    throw insertError
  }

  void onNewUserSignup()
  return mapSyncedProfile(insertedProfile)
}

export async function getUserRoles() {
  const { data: rolesUserData } = await supabase.auth.getUser()
  const user = rolesUserData.user

  if (!user) return []

  const { data, error } = await supabase
    .from('acl_user_roles')
    .select(`
      role:acl_roles (
        id,
        name
      )
    `)
    .eq('user_id', user.id)
    .eq('is_active', true)

  if (error) throw error

  return data.map(r => r.role?.name).filter(Boolean)
}

export async function getUserPermissions() {
  const { data } = await supabase
    .from('acl_role_menu_permissions') // FIXME: modify name for acl_user_permissions
    .select(`
      can_view,
      can_edit,
      menu_id,
      acl_menus (
        path
      )
    `)

  return (data || []).map((row) => ({
    canView: row.can_view,
    canEdit: row.can_edit,
    menuId: row.menu_id,
    path: normalizeAppPath(row.acl_menus?.path),
  }))
}

/**
 * Gets the list of navigation options from Supabase.
 * Uses DB function get_user_acl_menus so IMM users never receive UTM menus
 * (avoids sidebar flicker from client-side filtering).
 */
export async function getUserMenus(userId) {
  const { data, error } = await supabase.rpc('get_user_acl_menus', {
    p_user_id: userId,
  })

  if (error) {
    // Fallback while the migration is not applied yet.
    console.warn(
      'get_user_acl_menus RPC unavailable, falling back to join query:',
      error.message
    )
    return getUserMenusLegacy(userId)
  }

  return (data || [])
    .map((menu) => ({
      id: menu.id,
      name: menu.name,
      path: normalizeAppPath(menu.path),
      parentId: menu.parent_id,
      orderIndex: menu.order_index,
      isActive: menu.is_active,
      styles: menu.styles,
      canView: menu.can_view,
      canEdit: menu.can_edit,
    }))
    .filter((menu) => menu.isActive)
    .sort((a, b) => (a.orderIndex ?? 999) - (b.orderIndex ?? 999))
}

/**
 * Legacy ACL join (pre get_user_acl_menus). Kept as migration fallback only.
 */
async function getUserMenusLegacy(userId) {
  const { data, error } = await supabase
    .from('acl_user_roles')
    .select(`
      acl_roles (
        acl_role_menu_permissions (
          can_view,
          can_edit,
          acl_menus (
            id,
            name,
            path,
            parent_id,
            order_index,
            is_active,
            styles
          )
        )
      )
    `)
    .eq('user_id', userId)
    .eq('is_active', true)

  if (error) throw error
  if (!data || data.length === 0) return []

  const menuMap = new Map()

  data.forEach((userRole) => {
    const role = userRole.acl_roles
    if (!role?.acl_role_menu_permissions) return

    role.acl_role_menu_permissions.forEach((p) => {
      if (!p.can_view || !p.acl_menus) return

      const menu = p.acl_menus
      const existing = menuMap.get(menu.id)

      const mappedMenu = {
        id: menu.id,
        name: menu.name,
        path: normalizeAppPath(menu.path),
        parentId: menu.parent_id,
        orderIndex: menu.order_index,
        isActive: menu.is_active,
        styles: menu.styles,
        canView: p.can_view,
        canEdit: p.can_edit,
      }

      if (!existing) {
        menuMap.set(menu.id, mappedMenu)
      } else {
        menuMap.set(menu.id, {
          ...existing,
          canEdit: existing.canEdit || p.can_edit,
        })
      }
    })
  })

  return Array.from(menuMap.values())
    .filter((m) => m.isActive)
    .sort((a, b) => a.orderIndex - b.orderIndex)
}
